import { wccModules } from 'enums/wccModules';
import { isIE } from 'helpers/browser';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { NodeTracker as NodeTracker } from 'managers/nodes/tracker';
import { EffectsContainer } from 'mixins/withEffects';
import GridThresholdColumnPicker from './columnpickers/threshold';
import './grid.html';
import './grid.scss';
import { GridItemModel, GridItemState } from './item';
import GridStageVM, { GridStageVMConfig } from './stage';

components
    .registerLocal('grid-new-stage', () => require('components/grid/stage'))
    .registerLocal('grid-new-column', () => require('components/grid/column'))
    .registerLocal('grid-new-item', () => require('components/grid/item'));

export interface GridConfig<T> {
    source?: SubscribableOrNullableValue<Array<T>>
    columnWidth?: SubscribableOrNullableValue<number>
    offset?: SubscribableOrNullableValue<number>
    outerOffset?: SubscribableOrNullableValue<number>
    threshold?: SubscribableOrNullableValue<number>
    maxHeight?: SubscribableOrNullableValue<number>
    keepOrder?: SubscribableOrNullableValue<boolean>
    smoothResize?: SubscribableOrNullableValue<boolean>
    disableAnimations?: SubscribableOrNullableValue<boolean>
    lock?: SubscribableOrNullableValue<boolean>

    Source?: SubscribableOrNullableValue<Array<T>>
    ColumnWidth?: SubscribableOrNullableValue<number>
    Offset?: SubscribableOrNullableValue<number>
    OuterOffset?: SubscribableOrNullableValue<number>
    Threshold?: SubscribableOrNullableValue<number>
    MaxHeight?: SubscribableOrNullableValue<number>
    KeepOrder?: SubscribableOrNullableValue<boolean>
}

export class Grid<T = any> {
    vm = ko.observable<GridVM<T>>();
    hiddenItems = ko.observableArray<GridItemModel<T>>();
    rendering = ko.observable(false);

    source: Observable<Array<T>>

    columnWidth: Observable<number>
    offset: Observable<number>
    outerOffset: Observable<number>
    threshold: Observable<number>
    maxHeight: Observable<number>
    keepOrder: Observable<boolean>
    smoothResize: Observable<boolean>
    disableAnimations: Observable<boolean>
    lock: Observable<boolean>    
    
    realColumnWidth: Observable<number | undefined> = ko.observable();

    constructor(config: GridConfig<T>) {
        this.source = ko.observable(config.Source ?? config.source).unwrap().default(<Array<T>>[]).extend({ deferred: true });

        this.columnWidth = ko.observable(config.ColumnWidth || config.columnWidth).unwrap().default(300);
        this.offset = ko.observable(config.Offset || config.offset).unwrap().default(0);
        this.outerOffset = ko.observable(config.OuterOffset || config.outerOffset).unwrap().default(0);
        this.threshold = ko.observable(config.Threshold || config.threshold).unwrap().default(0);
        this.maxHeight = ko.observable(config.MaxHeight || config.maxHeight).unwrap().default(0);
        this.keepOrder = ko.observable(config.KeepOrder || config.keepOrder).unwrap().default(false);
        this.smoothResize = ko.observable(config.smoothResize).unwrap().default(false);
        this.disableAnimations = ko.observable(config.disableAnimations).unwrap().default(false);
        this.lock = ko.observable(config.lock).unwrap().default(false);
    }

    update(hard?: boolean) {
        this.vm.invokeNotNull(vm => vm.update(hard));
    }
}

@injectable()
export default class GridVM<T> {
    private columnsCount = ko.observable(1);

    private $element: JQuery
    private tracker: NodeTracker

    private width: Subscribable<number>
    private bodyHeight: Subscribable<number>
    private realColumnWidth: Subscribable<number | undefined>

    private items: Subscribable<Array<GridItemModel<T>>>;

    bodyNode = ko.observable<HTMLElement>();

    stageConfig: GridStageVMConfig<T>
    stage = ko.observable<GridStageVM<T>>();

    offset: Observable<number>
    outerOffset: Observable<number>
    smoothResize: Observable<boolean>

    animate: Subscribable<boolean>
    lock: Subscribable<boolean>
    isValid: Subscribable<boolean>
    isRendering: Subscribable<boolean>

    static Model = Grid;

    id = _.uniqueId('grid-new-');

    styles: Subscribable<string>

    ready: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) private model: Grid<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.$element = $(<HTMLElement>info.element);

        const source = model.source;

        this.tracker = new NodeTracker({ node: <HTMLElement>info.element });
        this.width = this.tracker.width;
        this.isValid = this.width.is(width => width > 0);

        this.offset = model.offset;
        this.outerOffset = model.outerOffset;
        this.smoothResize = model.smoothResize;
        this.animate = ko.pureComputed(() => !isIE && !model.disableAnimations());
        this.lock = ko.pureComputed(() => !this.isValid() || model.lock());        

        const { height: bodyHeight } = new NodeTracker({ node: this.bodyNode });
        this.bodyHeight = bodyHeight;

        this.realColumnWidth = ko.pureComputed(() => this.getRealColumnWidth()).extend({ deferred: true });

        this.items = source.map(data => {
            return new GridItemModel({
                index: source.indexOf(data),
                data: data,
                animate: this.animate
            });
        }).extend({ trackArrayChanges: true });

        const hiddenItems = this.items.filter(item => item.state() !== GridItemState.active).extend({ rateLimit: 0 });

        this.stageConfig = {
            items: this.items,
            columnsCount: this.columnsCount,
            maxHeight: model.maxHeight,
            keepOrder: model.keepOrder,
            columnsPicker: new GridThresholdColumnPicker({ threshold: model.threshold }),
            lock: this.lock
        };

        this.styles = ko.pureComputed(() => this.getStyles());

        this.isRendering = this.stage.pluck(s => s.isRendering, true);
        this.ready = ko.pureComputed(() => this.items().length > 0);

        effects.register([
            ko.computed(() => model.hiddenItems(hiddenItems()))
        ]);

        effects.register(() => {
            model.vm(this);
            return () => model.vm(undefined);
        });

        effects.register([
            ko.computed(() => model.rendering(this.isRendering())),
            ko.computed(() => model.realColumnWidth(this.realColumnWidth())),

            this.width.subscribe(_.debounce(() => this.stage()?.update(), 0)),

            this.items.onArrayChange({
                remove: item => item.dispose()
            }),

            ko.computed(() => this.renderNext()),
            ko.computed(this.processColumnsCount, this),
            ko.computed(this.processSmoothResize, this).extend({ deferred: true })
        ]);

        ko.applyBindingsToNode(info.element, {
            'visible': this.items.is(item => item.length > 0),

            'css': {
                'grid-new': true,
                'grid-new--smooth-resize': this.smoothResize
            },

            'attr': {
                'id': this.id
            }
        });
    }

    update(hard = false) {
        this.tracker.update();

        if (hard)
            this.stage()?.reset();
    }

    private renderNext() {
        const items = this.items();
        const isBusy = items.some(item => item.state() == GridItemState.pending);

        if (!isBusy) {
            const next = items.find(item => item.state() === GridItemState.justCreated);

            if (next != undefined)
                <GridItemModel<T>>next.state(GridItemState.pending);
        }
    }

    private getColumnsCount(targetWidth: number) {
        if (targetWidth > 0 && this.tracker.width() >= targetWidth)
            return Math.floor(this.tracker.width() / targetWidth);

        return 1;
    }

    private getStyles() {
        const columnsCount = this.columnsCount();
        const columnWidth = 100 / columnsCount;
        const columnOffset = this.offset();
        const offsetShrink = columnsCount > 1 ? (columnsCount - 1) / columnsCount : 0;
        const columnShrink = columnOffset * offsetShrink;

        return `
    #${this.id} {
        padding: ${this.outerOffset()}px;
    }

    #${this.id} .grid-new__column {
        width: calc(${columnWidth}% - ${columnShrink}px);
        margin-right: ${this.offset()}px;
    }

    #${this.id} .grid-new__column:last-child {
        margin-right: 0;
    }

    #${this.id} .grid-new__item {
        margin-bottom: ${this.offset()}px;
    }

    #${this.id} .grid-new__item:last-child {
        margin-bottom: 0;
    }`;
    }

    private getRealColumnWidth() {
        const columnsCount = this.columnsCount();
        const width = this.width();
        const outerOffset = this.outerOffset();
        const offset = this.offset();

        const availableSpace = width - outerOffset * 2 - offset * (columnsCount - 1);

        if (columnsCount > 0)
            return Math.floor(availableSpace / columnsCount);
    }

    private processColumnsCount() {
        const targetWidth = this.model.columnWidth();

        if (this.isValid())
            this.columnsCount(this.getColumnsCount(targetWidth));
    }

    private processSmoothResize() {
        if (this.smoothResize()) {
            const bodyHeight = this.bodyHeight();
            const outerOffset = this.outerOffset();

            requestAnimationFrame(() => {
                if (bodyHeight > 0) {
                    this.$element.css({ 'height': bodyHeight + outerOffset * 2 + 'px' });
                } else {
                    this.$element.css({ 'height': '' });
                }
            });
        }
    }
}